/**
 * Locale detection + override logic (issue #472).
 *
 * Pure module — no i18next import, no jsdom needed. All the "which locale"
 * decisions live here so they can be unit-tested in isolation and so the same
 * code path drives both the runtime detector (see ./index.ts) and the tests.
 *
 * Product decision: pt-PT is the primary locale, en-US the secondary. pt-PT
 * catalogs ship empty for now, so with `fallbackLng: en-US` every key falls
 * back to English and the rendered app stays English until translation (#473).
 */

export const LOCALE_STORAGE_KEY = 'clara-locale'

export const SUPPORTED_LOCALES = ['pt-PT', 'en-US'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

/** Primary locale — used when nothing else maps. */
export const DEFAULT_LOCALE: Locale = 'pt-PT'
/** Per-key translation fallback (empty pt-PT keys resolve to English). */
export const FALLBACK_LOCALE: Locale = 'en-US'

/** Language names are always shown in their own language. */
export const LOCALE_LABELS: Record<Locale, string> = {
  'pt-PT': 'Português (Portugal)',
  'en-US': 'English (US)',
}

export function isSupportedLocale(value: unknown): value is Locale {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  )
}

/**
 * Map a raw language tag onto a supported locale: any `pt*` → pt-PT, any
 * `en*` → en-US. Returns null for anything unmappable (the caller decides the
 * fallback).
 */
export function normalizeLocale(raw: string | null | undefined): Locale | null {
  if (!raw) return null
  const lower = raw.toLowerCase()
  if (lower.startsWith('pt')) return 'pt-PT'
  if (lower.startsWith('en')) return 'en-US'
  return null
}

/** Read + normalize the manual override in localStorage (guarded). */
export function readStoredLocale(): Locale | null {
  try {
    return normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY))
  } catch {
    // Storage blocked (private mode / denied) — treat as no override.
    return null
  }
}

/** Persist the manual override (guarded — non-fatal if storage is blocked). */
export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Ignore — the in-memory language change still applies for this session.
  }
}

function navigatorLocales(): readonly string[] {
  if (typeof navigator === 'undefined') return []
  const list = navigator.languages
  if (list && list.length > 0) return list
  return navigator.language ? [navigator.language] : []
}

/**
 * Resolve the active locale, in order:
 *   1. localStorage override (`clara-locale`) — a deliberate manual choice.
 *   2. Browser preference — the first navigator language that maps to a
 *      supported locale (any `pt*` → pt-PT, any `en*` → en-US).
 *   3. Default (pt-PT).
 */
export function detectLocale(): Locale {
  const stored = readStoredLocale()
  if (stored) return stored

  for (const lang of navigatorLocales()) {
    const mapped = normalizeLocale(lang)
    if (mapped) return mapped
  }

  return DEFAULT_LOCALE
}

/** Keep `<html lang>` in sync with the active locale (guarded). */
export function syncDocumentLang(locale: string): void {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = locale
  }
}
